import Phaser from "phaser";
import { GameModel } from "../core/model";
import { GridLayout } from "./GridLayout";

export class LaserRenderer {
  private graphics: Phaser.GameObjects.Graphics;
  private flashTimerMs = 0;

  constructor(private readonly scene: Phaser.Scene, private readonly layout: GridLayout) {
    this.graphics = scene.add.graphics();
    this.graphics.setDepth(200);
  }

  update(delta: number): void {
    if (this.flashTimerMs > 0) {
      this.flashTimerMs -= delta;
      if (this.flashTimerMs <= 0) {
        this.graphics.clear();
      }
    }
  }

  drawLaserBeam(model: GameModel, gridWidth: number, gridHeight: number): void {
    let path = model.lastLaserPath;

    // If nothing was actually hit, build a visual ray straight ahead
    if (!path || path.length === 0) {
      const fake: { x: number; y: number }[] = [];
      const head = model.snake.head;
      const segments = model.snake.segments;

      // Figure out current direction from head -> next segment
      let dx = 1;
      let dy = 0;
      if (segments.length > 1) {
        const neck = segments[1];
        dx = head.x - neck.x;
        dy = head.y - neck.y;
        if (dx !== 0) dx = dx > 0 ? 1 : -1;
        if (dy !== 0) dy = dy > 0 ? 1 : -1;
      }

      // March forward until we leave the grid
      let x = head.x;
      let y = head.y;
      const maxSteps = gridWidth + gridHeight;
      for (let i = 0; i < maxSteps; i++) {
        x += dx;
        y += dy;
        if (x < 0 || x >= gridWidth || y < 0 || y >= gridHeight) break;
        fake.push({ x, y });
      }

      path = fake;
    }

    this.graphics.clear();
    this.graphics.lineStyle(2, 0xff0000, 1);

    const head = model.snake.head;
    const headWorld = this.layout.gridToWorld(head.x, head.y);
    this.graphics.beginPath();
    this.graphics.moveTo(headWorld.wx, headWorld.wy);

    for (const pos of path) {
      const { wx, wy } = this.layout.gridToWorld(pos.x, pos.y);
      this.graphics.lineTo(wx, wy);
    }

    this.graphics.strokePath();
    this.flashTimerMs = 150; // ~150ms
  }

  /** Draw a beam from an arbitrary origin (used for hunter/enemy lasers). */
  drawEnemyLaserBeam(origin: { x: number; y: number }, path: { x: number; y: number }[]): void {
    if (!path || path.length === 0) return;

    // Don’t clear here so multiple enemy beams can render in one frame.
    this.graphics.lineStyle(2, 0xff4444, 1);

    const start = this.layout.gridToWorld(origin.x, origin.y);
    this.graphics.beginPath();
    this.graphics.moveTo(start.wx, start.wy);

    for (const pos of path) {
      const { wx, wy } = this.layout.gridToWorld(pos.x, pos.y);
      this.graphics.lineTo(wx, wy);
    }

    this.graphics.strokePath();
    this.flashTimerMs = 150;
  }
}
